import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, privateFileUrl } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { RegenLogo } from '@/components/RegenLogo';
import { ArrowRight, ArrowLeft, Check, Target, Dumbbell, Salad, Camera, Upload, Lock, ShieldAlert } from 'lucide-react';
import { IF_QUESTION, IF_HELP, scheduleFor } from '@/components/MealPreferences';

const ACCENT = '#00C8FF';

const DEFAULT_MEALS = { 'Lose Fat': '3', 'Build Muscle': '4', 'Strength & Longevity': '3' };
function mealsHelp(d) {
  const rec = d.primary_goal === 'Build Muscle' ? 'Recommended for Build Muscle: 4 meals (you can choose 3).'
    : d.primary_goal ? `Default for ${d.primary_goal}: 3 meals (you can choose 4).` : '';
  return `${rec}${d.meals_per_day === '4' ? ` Your day: ${scheduleFor(4).join(', ')}.` : ''}`;
}

// Goal → default meal count (until the user picks one); 4 meals never uses intermittent fasting.
export function applyOnboardingAnswer(d, k, v) {
  const n = { ...d, [k]: v };
  if (k === 'primary_goal' && !d._meals_chosen) n.meals_per_day = DEFAULT_MEALS[v] || '3';
  if (k === 'meals_per_day') { n._meals_chosen = true; if (v === '4') delete n.intermittent_fasting; }
  return n;
}
export const isFieldShown = (f, d) => !f.showIf || f.showIf(d);

// Returns the first problem on a step (or '') — only visible fields are ever required.
export function stepProblem(cur, data) {
  if (cur.photos) return '';
  for (const f of cur.fields) {
    if (!isFieldShown(f, data)) continue;
    const val = data[f.key];
    if (f.required && (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0))) return `Please complete: ${f.label}`;
    const otherPicked = val === 'Other' || (Array.isArray(val) && val.includes('Other'));
    if (f.other && otherPicked && !String(data[`${f.key}_other`] ?? '').trim()) return `Please specify "Other" for: ${f.label}`;
    if (f.specifyOn && val === f.specifyOn && !String(data[f.specifyKey] ?? '').trim()) return `${f.specifyLabel} for: ${f.label}`;
  }
  return '';
}
export function validateOnboardingStep(cur, data) {
  const p = stepProblem(cur, data);
  if (p) toast.error(p);
  return !p;
}
export const firstIncompleteStep = (data) => {
  const i = STEPS.findIndex(st => !st.photos && stepProblem(st, data));
  return i === -1 ? STEPS.length - 1 : i;
};

export function StepFields({ cur, data, set }) {
  return (
    <>
      {cur.notice && (
        <div className="flex items-start gap-2.5 p-4 rounded-xl border border-[#FFB800]/40 bg-[#FFB800]/10" data-testid="allergy-notice">
          <ShieldAlert className="w-4 h-4 text-[#B7791F] mt-0.5 flex-shrink-0" />
          <p className="text-xs leading-relaxed text-foreground/80">{cur.notice}</p>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-6">
        {cur.fields.map(f => <FieldRenderer key={f.key} field={f} data={data} set={set} />)}
      </div>
    </>
  );
}
export const onboardingPayload = ({ _meals_chosen, ...d }) => d;

// ---- Step definitions (field-driven) ----
// Only answers used by programme generation, safety review or personalisation are asked.
export const ALLERGY_NOTICE = 'Meals are not screened for allergies or intolerances. If you have a food allergy, please contact us at hello@yourregen.com before starting your meal plan.';

export const STEPS = [
  {
    title: 'Goals & Body', icon: Target, color: '#65A30D',
    fields: [
      { key: 'primary_goal', label: 'Primary goal', type: 'radio', options: ['Lose Fat', 'Build Muscle', 'Strength & Longevity'], required: true },
      { key: 'gender', label: 'Gender', type: 'radio', options: ['Male', 'Female', 'Other'], other: true, required: true },
      { key: 'age', label: 'Age', type: 'number', required: true },
      { key: 'height_cm', label: 'Height (cm)', type: 'number', required: true },
      { key: 'current_weight_kg', label: 'Current weight (kg)', type: 'number', required: true },
      { key: 'target_weight_kg', label: 'Target weight (kg)', type: 'number', required: true, showIf: d => d.primary_goal !== 'Strength & Longevity' },
    ],
  },
  {
    title: 'Training Profile', icon: Dumbbell, color: '#00C8FF',
    fields: [
      { key: 'activity_level', label: 'Current activity level', type: 'radio', options: ['Sedentary', 'Lightly active', 'Moderately active', 'Very active'], required: true },
      { key: 'training_experience', label: 'Training experience', type: 'radio', options: ['Beginner', 'Intermediate', 'Advanced'], required: true },
      { key: 'training_types', label: 'What do you currently do? (select all)', type: 'multi', options: ['Strength', 'Cardio', 'Sports', 'Yoga / Mobility', 'None', 'Other'], other: true, required: true },
      { key: 'days_per_week', label: 'Days per week you can train', type: 'radio', options: ['1', '2', '3', '4', '5', '6+'], required: true, help: 'Total sessions per week, including any HIIT or Fitness Racing. You can add extra sessions later.' },
      { key: 'equipment_access', label: 'Where will you train?', type: 'radio', options: ['Home Training (Minimal Equipment)', 'Gym Training'], required: true, help: 'Home training uses your body weight and minimal equipment such as dumbbells.' },
      { key: 'fitness_racing_interest', label: 'Interested in Fitness Racing training?', type: 'radio', options: ['Yes', 'No'], required: true, help: 'Fitness Racing combines running or cardio with functional strength and conditioning exercises performed in circuits. It is designed for people preparing for fitness racing competitions or who simply enjoy this type of challenging hybrid training. This preference is independent of your primary goal.' },
      { key: 'injuries', label: 'Any injuries or physical limitations?', type: 'radio', options: ['No', 'Yes'], specifyOn: 'Yes', specifyKey: 'injuries_detail', specifyLabel: 'Please specify', required: true },
      { key: 'medical_conditions', label: 'Any medical conditions?', type: 'radio', options: ['No', 'Yes'], specifyOn: 'Yes', specifyKey: 'medical_conditions_detail', specifyLabel: 'Please specify', required: true },
    ],
  },
  {
    title: 'Nutrition', icon: Salad, color: '#65A30D', notice: ALLERGY_NOTICE,
    fields: [
      { key: 'meals_per_day', label: 'How many meals per day do you prefer?', type: 'radio', options: ['3', '4'], required: true, help: mealsHelp },
      { key: 'intermittent_fasting', label: IF_QUESTION, type: 'radio', options: ['Yes', 'No'], required: true, showIf: d => d.meals_per_day === '3',
        help: d => `${IF_HELP} Yes: ${scheduleFor(3, true).join(', ')}. No: ${scheduleFor(3, false).join(', ')}.` },
    ],
  },
  { title: 'Progress Photos & Finish', icon: Camera, color: '#A78BFA', photos: true },
];

export default function Questionnaire() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ full_name: user?.name || '', country: user?.country || '' });
  const [photos, setPhotos] = useState({}); // { front: {photo_url}, side, back }
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setData(d => applyOnboardingAnswer(d, k, v));
  const cur = STEPS[step];
  const pct = Math.round(((step + 1) / STEPS.length) * 100);

  const validateStep = () => validateOnboardingStep(cur, data);

  const next = async () => {
    if (!validateStep()) return;
    if (step < STEPS.length - 1) { setStep(s => s + 1); window.scrollTo(0, 0); return; }
    // Final submit
    setSubmitting(true);
    try {
      const payload = {
        ...onboardingPayload(data),
        photos: {
          front: photos.front?.photo_url, side: photos.side?.photo_url, back: photos.back?.photo_url,
        },
      };
      await api.post('/onboarding', { data: payload });
      await refresh();
      toast.success('Onboarding complete! Your program is being prepared.');
      nav('/app/home');
    } catch (e) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const back = () => { if (step > 0) { setStep(s => s - 1); window.scrollTo(0, 0); } };

  return (
    <div className="min-h-screen max-w-5xl mx-auto px-5 md:px-10 pt-10 pb-32">
      <div className="flex items-center justify-between">
        <RegenLogo size="sm" />
        <div className="label-mini text-muted-foreground">Step {step + 1} of {STEPS.length}</div>
      </div>

      {/* progress bar */}
      <div className="mt-5 h-1.5 rounded-full bg-[var(--c-film)] overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: ACCENT }} />
      </div>

      <div className="mt-8 flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: `${cur.color}1a`, color: cur.color }}>
          <cur.icon className="w-5 h-5" />
        </div>
        <div>
          <div className="label-mini" style={{ color: cur.color }}>Onboarding</div>
          <h1 className="font-display font-black text-3xl tracking-tight">{cur.title}</h1>
        </div>
      </div>

      <div className="mt-7 space-y-6" data-testid={`onboarding-step-${step + 1}`}>
        {cur.photos ? (
          <PhotoStep photos={photos} setPhotos={setPhotos} />
        ) : (
          <StepFields cur={cur} data={data} set={set} />
        )}
      </div>

      {/* nav buttons */}
      <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-background via-background to-transparent pt-8 pb-6 px-5">
        <div className="max-w-5xl mx-auto flex gap-3 md:px-5">
          {step > 0 && (
            <Button onClick={back} variant="outline" data-testid="onboarding-back-btn" className="h-13 px-5 border-[var(--c-hair)] rounded-2xl">
              <ArrowLeft className="w-4 h-4" />
            </Button>
          )}
          <Button onClick={next} disabled={submitting} data-testid="onboarding-next-btn"
            className="flex-1 h-13 bg-[#00C8FF] hover:bg-[#00C8FF]/90 text-[#081726] font-semibold rounded-2xl">
            {step === STEPS.length - 1 ? (submitting ? 'Submitting…' : 'Submit & Finish') : 'Continue'}
            {!submitting && <ArrowRight className="ml-2 w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <label className="block text-sm font-semibold mb-2.5">{children}</label>;
}

export function FieldRenderer({ field, data, set }) {
  const val = data[field.key];
  if (!isFieldShown(field, data)) return null;
  const help = typeof field.help === 'function' ? field.help(data) : field.help;
  return (
    <div data-testid={`field-${field.key}`} className={field.type === 'number' || field.type === 'text' ? '' : 'md:col-span-2'}>
      <Label>{field.label}{field.required && <span className="text-[#00C8FF]"> *</span>}</Label>
      {help && <p className="text-xs text-muted-foreground -mt-1.5 mb-3 leading-relaxed" data-testid={`help-${field.key}`}>{help}</p>}

      {(field.type === 'text' || field.type === 'number') && (
        <Input type={field.type} value={val ?? ''} onChange={e => set(field.key, e.target.value)}
          data-testid={`input-${field.key}`} className="bg-card border-border h-12 rounded-xl" />
      )}

      {field.type === 'textarea' && (
        <Textarea value={val ?? ''} onChange={e => set(field.key, e.target.value)}
          data-testid={`input-${field.key}`} rows={3} className="bg-card border-border rounded-xl" />
      )}

      {field.type === 'scale' && (
        <div>
          <div className="flex gap-1.5">
            {[1,2,3,4,5,6,7,8,9,10].map(n => (
              <button key={n} type="button" onClick={() => set(field.key, n)}
                data-testid={`scale-${field.key}-${n}`}
                className={`flex-1 h-10 rounded-lg text-sm font-mono font-bold transition ${val === n ? 'bg-[#00C8FF] text-[#081726]' : 'bg-card text-muted-foreground hover:bg-muted'}`}>
                {n}
              </button>
            ))}
          </div>
          <div className="flex justify-between text-[11px] text-muted-foreground mt-1.5"><span>Low</span><span>High</span></div>
        </div>
      )}

      {field.type === 'radio' && (
        <div className="grid grid-cols-2 gap-2.5">
          {field.options.map(opt => (
            <button key={opt} type="button" onClick={() => set(field.key, opt)}
              data-testid={`opt-${field.key}-${opt.replace(/\s+/g,'-').toLowerCase()}`}
              className={`px-4 py-3 rounded-xl text-sm font-medium text-left transition border ${val === opt ? 'border-[#00C8FF] bg-[#00C8FF]/10 text-foreground' : 'border-border bg-card text-muted-foreground hover:border-foreground/30'}`}>
              <span className="inline-flex items-center gap-2">
                <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${val === opt ? 'border-[#00C8FF] bg-[#00C8FF]' : 'border-[var(--c-hair)]'}`}>
                  {val === opt && <Check className="w-2.5 h-2.5 text-[#081726]" strokeWidth={4} />}
                </span>
                {opt}
              </span>
            </button>
          ))}
        </div>
      )}

      {field.type === 'multi' && (
        <div className="grid grid-cols-2 gap-2.5">
          {field.options.map(opt => {
            const arr = Array.isArray(val) ? val : [];
            const on = arr.includes(opt);
            return (
              <button key={opt} type="button"
                onClick={() => set(field.key, on ? arr.filter(o => o !== opt) : [...arr, opt])}
                data-testid={`opt-${field.key}-${opt.replace(/\s+/g,'-').toLowerCase()}`}
                className={`px-4 py-3 rounded-xl text-sm font-medium text-left transition border ${on ? 'border-[var(--c-green)] bg-[var(--c-green-soft)] text-foreground' : 'border-border bg-card text-muted-foreground hover:border-foreground/30'}`}>
                <span className="inline-flex items-center gap-2">
                  <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? 'border-[var(--c-green)] bg-[var(--c-green)]' : 'border-[var(--c-hair)]'}`}>
                    {on && <Check className="w-2.5 h-2.5 text-[#081726]" strokeWidth={4} />}
                  </span>
                  {opt}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Other → please specify (mandatory) */}
      {field.other && ((field.type === 'radio' && val === 'Other') || (field.type === 'multi' && Array.isArray(val) && val.includes('Other'))) && (
        <Input value={data[`${field.key}_other`] ?? ''} onChange={e => set(`${field.key}_other`, e.target.value)}
          placeholder="Please specify…" data-testid={`input-${field.key}-other`}
          className="bg-card border-[#00C8FF]/40 h-12 rounded-xl mt-2.5" />
      )}

      {/* Conditional specify (e.g. Yes → detail) */}
      {field.specifyOn && val === field.specifyOn && (
        <Textarea value={data[field.specifyKey] ?? ''} onChange={e => set(field.specifyKey, e.target.value)}
          placeholder={field.specifyLabel} rows={2} data-testid={`input-${field.specifyKey}`}
          className="bg-card border-[#00C8FF]/40 rounded-xl mt-2.5" />
      )}
    </div>
  );
}

export function PhotoStep({ photos, setPhotos }) {
  const slots = [
    { key: 'front', label: 'Front' },
    { key: 'side', label: 'Side' },
    { key: 'back', label: 'Back' },
  ];
  return (
    <div>
      <div className="grid grid-cols-3 gap-3">
        {slots.map(s => <PhotoSlot key={s.key} slot={s} photos={photos} setPhotos={setPhotos} />)}
      </div>
      <div className="mt-5 flex items-start gap-2.5 p-4 rounded-xl bg-card">
        <Lock className="w-4 h-4 text-[#A78BFA] mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Photos are private and only visible to your coach. They help us evaluate your body composition and track your transformation accurately. Optional — you can add or update these anytime from your Profile.
        </p>
      </div>
    </div>
  );
}

function PhotoSlot({ slot, photos, setPhotos }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const existing = photos[slot.key];

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('angle', slot.key);
      fd.append('note', 'onboarding');
      const r = await api.post('/progress-photos/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotos(p => ({ ...p, [slot.key]: r.data }));
    } catch {
      toast.error('Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const src = existing ? privateFileUrl(existing.photo_url) : null;

  return (
    <button type="button" onClick={() => ref.current?.click()} data-testid={`photo-slot-${slot.key}`}
      className={`relative aspect-[3/4] rounded-2xl border-2 border-dashed flex flex-col items-center justify-center overflow-hidden transition ${existing ? 'border-[var(--c-green)]' : 'border-[var(--c-hair)] hover:border-[var(--c-hair)]'}`}>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid={`photo-input-${slot.key}`} />
      {src ? (
        <>
          <img src={src} alt={slot.label} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[var(--c-green)] flex items-center justify-center">
            <Check className="w-3.5 h-3.5 text-[#081726]" strokeWidth={4} />
          </div>
        </>
      ) : (
        <>
          {busy ? <Upload className="w-6 h-6 text-muted-foreground animate-pulse" /> : <Camera className="w-6 h-6 text-muted-foreground" />}
          <span className="text-xs text-muted-foreground mt-2">{busy ? 'Uploading…' : 'Add photo'}</span>
        </>
      )}
      <span className="absolute bottom-0 inset-x-0 text-center text-[11px] font-semibold py-1.5 bg-black/55 text-white">{slot.label}</span>
    </button>
  );
}
